import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Import translations directly
import enTranslations from "../translations/en.json";
import deTranslations from "../translations/de.json";

export type Language = "en" | "de";

export type TranslationLookup = (
  key: string,
  params?: Record<string, string>,
) => string;

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationLookup;
  translationsLoaded: boolean;
  getLocalizedPath: (path: string) => string;
};

export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

// Define the nested structure for translations
interface TranslationObject {
  [key: string]: string | TranslationObject;
}

// Initialize translations statically rather than loading asynchronously
const initialTranslations: Record<string, TranslationObject> = {
  en: enTranslations,
  de: deTranslations,
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState<Language>("en");
  const [translations] =
    useState<Record<string, TranslationObject>>(initialTranslations);
  const [translationsLoaded] = useState(true); // Always true since we load synchronously

  // Detect language from URL on initial load and route changes
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (firstSegment === "de") {
      setLanguageState("de");
    } else if (firstSegment === "en") {
      setLanguageState("en");
    } else {
      // Default to English for paths without language prefix
      setLanguageState("en");
    }
  }, [location.pathname]);

  // Function to set language and update URL
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);

    // Update URL to reflect language change
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];
    let newPath: string;

    // If current path already has a language prefix, replace it
    if (firstSegment === "en" || firstSegment === "de") {
      newPath =
        lang === "en"
          ? ["", ...pathSegments.slice(1)].join("/")
          : ["", lang, ...pathSegments.slice(1)].join("/");
    } else if (lang === "de") {
      // Add language prefix for German
      newPath = `/de${location.pathname}`;
    } else {
      // For English and root path
      newPath = location.pathname;
    }

    // Preserve query parameters
    if (location.search) {
      newPath += location.search;
    }

    navigate(newPath);
  };

  // Function to get localized path based on current language
  const getLocalizedPath = (path: string): string => {
    // Extract any query parameters from the path
    const [basePath, queryString] = path.split("?");

    let localizedPath: string;
    if (basePath === "/") {
      localizedPath = language === "en" ? "/" : `/${language}`;
    } else {
      localizedPath = language === "en" ? basePath : `/${language}${basePath}`;
    }

    // Add query parameters back if they exist
    if (queryString) {
      localizedPath += `?${queryString}`;
    }

    return localizedPath;
  };

  // Recursive function to get a nested translation value using dot notation
  const getNestedTranslation = (
    obj: TranslationObject,
    path: string[],
  ): string | undefined => {
    if (!obj || path.length === 0) return undefined;

    const [key, ...rest] = path;
    const value = obj[key];

    if (rest.length === 0) {
      return typeof value === "string" ? value : undefined;
    }

    if (typeof value === "object" && value !== null) {
      return getNestedTranslation(value as TranslationObject, rest);
    }

    return undefined;
  };

  // Enhanced translation function with better fallback handling
  const t = (key: string, params?: Record<string, string>): string => {
    // Special cases
    if (key === "common.language") {
      return language;
    }

    // Try to get translation in current language
    const keyPath = key.split(".");
    let translation = getNestedTranslation(translations[language], keyPath);
    if (translation) {
      // Replace placeholders with provided params
      if (params) {
        Object.keys(params).forEach((paramKey) => {
          const regex = new RegExp(`\\{${paramKey}\\}`, "g");
          translation = translation.replace(regex, params[paramKey]);
        });
      }
      return translation;
    }

    // For German, try to get from English as fallback
    if (language === "de" && translations["en"]) {
      const fallbackTranslation = getNestedTranslation(
        translations["en"],
        keyPath,
      );
      if (fallbackTranslation) {
        console.debug(`[Translation] Using English fallback for: ${key}`);
        return fallbackTranslation;
      }
    }

    // Try flattened format as last resort (for backwards compatibility)
    const flatKey = key.replace(/\./g, "_");
    if (
      translations[language][flatKey] &&
      typeof translations[language][flatKey] === "string"
    ) {
      console.debug(`[Translation] Using flattened key format: ${flatKey}`);
      return translations[language][flatKey] as string;
    }

    // Log missing translations to help with debugging
    console.debug(
      `[Translation] Missing translation for: ${key} in ${language}`,
    );

    // Return key as last resort
    return key;
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t, translationsLoaded, getLocalizedPath }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
